import React, { useEffect, ReactNode, useRef } from 'react';
import posthog from 'posthog-js';
import { useSessionStore } from '../../../state/src/useSessionStore';
import type { SessionState } from '../../../state/src/useSessionStore';
import { initializeFetchInterceptor } from '../utils/fetchInterceptor';
import { setupInputValueCapture, initializeErrorTracking, capturePosthogEvent, identifyPosthogPerson, registerPosthogSuperProperties } from '../utils/analytics';
import { getPostHogConfig } from '../config';

export const AnalyticsProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const agentId = useSessionStore((s: SessionState) => s.agentId);
  const unitProfileId = useSessionStore((s: SessionState) => s.unitProfileId);
  const kioskId = useSessionStore((s: SessionState) => (s as any).kioskId);
  const phoneNumber = useSessionStore((s: SessionState) => s.phoneNumber);
  const partnerName = useSessionStore((s: SessionState) => s.partnerName);
  const partnerAddress = useSessionStore((s: SessionState) => s.partnerAddress);
  const partnerCity = useSessionStore((s: SessionState) => (s as any).partnerCity);
  const partnerCountry = useSessionStore((s: SessionState) => (s as any).partnerCountry);
  const partnerState = useSessionStore((s: SessionState) => (s as any).partnerState);
  const partnerPostalCode = useSessionStore((s: SessionState) => (s as any).partnerPostalCode);

  useEffect(() => {
    // Get PostHog configuration from config files
    const config = getPostHogConfig();

    // Only initialize PostHog if we have an API key
    if (config.apiKey && typeof window !== 'undefined') {
      posthog.init(config.apiKey, {
        api_host: config.apiHost,
        debug: config.isDevelopment, // Only show logs in development
        capture_pageview: true,
        capture_pageleave: true,
        capture_performance: true,
        capture_exceptions: true,
        enable_heatmaps: true,
        capture_heatmaps: true,
        autocapture: {
          dom_event_allowlist: ['click'],
          element_allowlist: ['button', 'input', 'select'],
          css_selector_allowlist: ['button', 'input[type="radio"]', 'select']
        },
        loaded: (posthog: any) => {
          // Set global PostHog instance for input capture
          if (typeof window !== 'undefined') {
            window.posthog = posthog;
          }
          
          initializeErrorTracking();
          initializeFetchInterceptor();
          setupInputValueCapture();
        }
      });
    }
  }, []);

  // Separate effect to handle user identification when kioskId or phoneNumber changes
  useEffect(() => {
    if (typeof window !== 'undefined' && window.posthog) {
      const baseProperties = {
        'data-mg-kiosk-id': kioskId,
        'data-mg-kiosk-profile-id': unitProfileId,
        'data-mg-agent-id': agentId,
      };

      if (phoneNumber && kioskId) {
        // User has entered phone number (likely in send flow) - use phone as primary identifier
        identifyPosthogPerson(phoneNumber, {
          ...baseProperties,
          'data-mg-phone-number': phoneNumber,
          'data-mg-identification-method': 'PHONE NUMBER',
        });

        registerPosthogSuperProperties({
          ...baseProperties,
          'data-mg-phone-number': phoneNumber,
          'data-mg-identification-method': 'PHONE NUMBER',
          username: phoneNumber,
          'data-mg-partner-name': partnerName,
          'data-mg-partner-address': partnerAddress,
          'data-mg-partner-city': partnerCity,
          'data-mg-partner-country': partnerCountry,
          'data-mg-partner-state': partnerState,
          'data-mg-partner-postal-code': partnerPostalCode,
        });
      } else if (kioskId) {
        capturePosthogEvent('QR Code Scanned', {
          ...baseProperties,
          'data-mg-identification-method': 'KIOSK ID',
        });

        registerPosthogSuperProperties({
          ...baseProperties,
          'data-mg-identification-method': 'KIOSK ID',
          'data-mg-partner-name': partnerName,
          'data-mg-partner-address': partnerAddress,
          'data-mg-partner-city': partnerCity,
          'data-mg-partner-country': partnerCountry,
          'data-mg-partner-state': partnerState,
          'data-mg-partner-postal-code': partnerPostalCode,
        });
      }
    }
  }, [kioskId, agentId, unitProfileId, phoneNumber, partnerName, partnerAddress, partnerCity, partnerCountry, partnerState, partnerPostalCode]);

  return <>{children}</>;
};